<!-- app/javascript/dashboard/routes/dashboard/bulkmessage/components/CsvProcessingProgress.vue -->

<script setup>
import { computed } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  progress: {
    type: Number,
    default: 0,
  },
  stage: {
    type: String,
    default: '',
  },
  processedRows: {
    type: Number,
    default: 0,
  },
  totalRows: {
    type: Number,
    default: 0,
  },
  fileName: {
    type: String,
    default: '',
  },
});

const progressText = computed(() => {
  if (props.totalRows > 0) {
    return `${props.stage} ${props.processedRows.toLocaleString()} / ${props.totalRows.toLocaleString()} linhas`;
  }
  return props.stage;
});

const formattedFileName = computed(() => {
  if (!props.fileName) return '';
  if (props.fileName.length > 30) {
    return props.fileName.substring(0, 27) + '...';
  }
  return props.fileName;
});

const progressBarStyle = computed(() => ({
  width: `${Math.min(100, Math.max(0, props.progress))}%`,
}));
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black75 dark:bg-n-alpha-black75 backdrop-blur-sm"
  >
    <div
      class="relative w-full max-w-md bg-n-solid-1 dark:bg-n-solid-1 border border-n-slate-6 dark:border-n-slate-6 shadow-2xl rounded-xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-center px-6 py-4 border-b border-n-slate-6 dark:border-n-slate-6 bg-n-slate-2 dark:bg-n-slate-2"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 bg-gradient-to-br from-n-iris-9 to-n-iris-10 dark:from-n-iris-9 dark:to-n-iris-10 rounded-lg flex items-center justify-center shadow-md"
          >
            <div class="relative">
              <i class="i-lucide-file-text text-white text-base" />
              <div
                class="absolute -top-1 -right-1 w-3 h-3 bg-n-teal-9 rounded-full animate-pulse"
              />
            </div>
          </div>
          <div class="text-center">
            <h3 class="text-lg font-bold text-n-slate-12 dark:text-n-slate-12">
              Processando Arquivo
            </h3>
            <p class="text-sm text-n-slate-11 dark:text-n-slate-11 font-medium">
              Importando contatos do CSV
            </p>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="px-6 py-6 space-y-4">
        <!-- File Info -->
        <div
          v-if="fileName"
          class="flex items-center gap-3 p-3 bg-n-slate-2 dark:bg-n-slate-2 rounded-lg"
        >
          <div
            class="w-8 h-8 bg-n-iris-3 dark:bg-n-iris-3 rounded flex items-center justify-center"
          >
            <i class="i-lucide-file text-n-iris-11 text-sm" />
          </div>
          <div class="flex-1 min-w-0">
            <p
              class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12 truncate"
              :title="fileName"
            >
              {{ formattedFileName }}
            </p>
            <p class="text-xs text-n-slate-11 dark:text-n-slate-11">
              Arquivo CSV/Excel
            </p>
          </div>
        </div>

        <!-- Progress Bar -->
        <div class="space-y-2">
          <div class="flex justify-between items-center">
            <span class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12">
              Progresso
            </span>
            <span class="text-sm text-n-slate-11 dark:text-n-slate-11">
              {{ progress }}%
            </span>
          </div>
          <div
            class="w-full h-3 bg-n-slate-3 dark:bg-n-slate-3 rounded-full overflow-hidden"
          >
            <div
              class="h-full bg-gradient-to-r from-n-iris-9 to-n-iris-10 dark:from-n-iris-9 dark:to-n-iris-10 rounded-full transition-all duration-300 ease-out"
              :style="progressBarStyle"
            />
          </div>
        </div>

        <!-- Status Text -->
        <div
          class="p-3 bg-gradient-to-r from-n-iris-2 to-n-iris-3 dark:from-n-iris-2 dark:to-n-iris-3 border border-n-iris-6 dark:border-n-iris-6 rounded-lg"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-6 h-6 bg-gradient-to-br from-n-iris-9 to-n-iris-10 dark:from-n-iris-9 dark:to-n-iris-10 rounded flex items-center justify-center"
            >
              <i class="i-lucide-loader-2 text-white text-xs animate-spin" />
            </div>
            <div class="flex-1">
              <p
                class="text-sm font-medium text-n-iris-12 dark:text-n-iris-12"
              >
                {{ progressText }}
              </p>
              <p class="text-xs text-n-iris-11 dark:text-n-iris-11 mt-1">
                Por favor, aguarde enquanto processamos seu arquivo
              </p>
            </div>
          </div>
        </div>

        <!-- Performance Info for Large Files -->
        <div
          v-if="totalRows > 5000"
          class="p-3 bg-gradient-to-r from-n-amber-2 to-n-amber-3 dark:from-n-amber-2 dark:to-n-amber-3 border border-n-amber-6 dark:border-n-amber-6 rounded-lg"
        >
          <div class="flex items-start gap-3">
            <div
              class="w-6 h-6 bg-n-amber-9 dark:bg-n-amber-9 rounded flex items-center justify-center flex-shrink-0"
            >
              <i class="i-lucide-info text-white text-xs" />
            </div>
            <div>
              <p
                class="text-sm font-medium text-n-amber-12 dark:text-n-amber-12 mb-1"
              >
                Arquivo grande detectado
              </p>
              <p class="text-xs text-n-amber-11 dark:text-n-amber-11">
                Processando {{ totalRows.toLocaleString() }} linhas. O
                processamento pode levar alguns instantes para garantir melhor
                performance.
              </p>
            </div>
          </div>
        </div>

        <!-- Animation Elements -->
        <div class="flex justify-center space-x-1 mt-4">
          <div
            class="w-2 h-2 bg-n-iris-9 rounded-full animate-bounce"
            style="animation-delay: 0ms"
          />
          <div
            class="w-2 h-2 bg-n-iris-9 rounded-full animate-bounce"
            style="animation-delay: 150ms"
          />
          <div
            class="w-2 h-2 bg-n-iris-9 rounded-full animate-bounce"
            style="animation-delay: 300ms"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-8px);
  }
  60% {
    transform: translateY(-4px);
  }
}

.animate-bounce {
  animation: bounce 1s infinite;
}
</style>